import { ChangeEvent, useRef } from 'react';

import { Trash2Icon } from 'lucide-react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { QUESTION_SET_LIMITS } from '@/util/validation/constants';
import { QuestionSetForm } from '@/util/validation/schemas/question-set-schemas';

import { toOptionValue } from './questionSetForm';
import { ShowIfFields } from './ShowIfFields';

type Props = {
  questionIndex: number;
  optionIndex: number;
  canRemove: boolean;
  onCommit: () => void;
  onRemove: (optionIndex: number) => void;
  onRenameValue: (from: string, to: string) => void;
};

export const OptionFields = ({
  questionIndex,
  optionIndex,
  canRemove,
  onCommit,
  onRemove,
  onRenameValue,
}: Props) => {
  const { t } = useTranslation();
  const { control, getValues, setValue, trigger } = useFormContext<QuestionSetForm>();
  const path = `questions.${questionIndex}.options.${optionIndex}` as const;
  const valueAtFocus = useRef('');

  // The value follows the label until the builder edits the value by hand.
  const handleLabelChange = (
    event: ChangeEvent<HTMLInputElement>,
    onChange: (e: unknown) => void
  ) => {
    const previousLabel = getValues(`${path}.label`);
    const value = getValues(`${path}.value`);
    if (!value || value === toOptionValue(previousLabel)) {
      setValue(`${path}.value`, toOptionValue(event.target.value));
    }
    onChange(event);
  };

  // Once per edit, from the value at focus: a value passed while typing may belong to another
  // option. Focus events bubble, so this covers the label and the value inputs.
  const handleFocus = () => {
    valueAtFocus.current = getValues(`${path}.value`);
  };
  const handleBlur = () => {
    const value = getValues(`${path}.value`);
    if (value !== valueAtFocus.current) {
      onRenameValue(valueAtFocus.current, value);
      trigger(`${path}.value`);
    }
  };

  return (
    <div
      className="flex flex-col gap-2 rounded-md border border-border p-2"
      onFocus={handleFocus}
      onBlur={handleBlur}
    >
      <div className="flex items-center justify-between">
        <span className="text-xs leading-4 font-semibold text-muted-foreground uppercase">
          {t('dialog.question_set.default_option', { 0: optionIndex + 1 })}
        </span>
        <Button
          className="hover:text-destructive hover:[&_svg]:text-destructive"
          type="button"
          variant="ghost-neutral"
          size="icon-sm"
          aria-label={t('dialog.question_set.remove_option')}
          disabled={!canRemove}
          onClick={() => onRemove(optionIndex)}
        >
          <Trash2Icon size={14} />
        </Button>
      </div>

      <FormField
        control={control}
        name={`${path}.label`}
        render={({ field }) => (
          <FormItem>
            <FormLabel>{t('dialog.question_set.option_label')}</FormLabel>
            <FormControl>
              <Input
                {...field}
                maxLength={QUESTION_SET_LIMITS.LABEL}
                onChange={(event) => handleLabelChange(event, field.onChange)}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={control}
        name={`${path}.value`}
        render={({ field }) => (
          <FormItem>
            <FormLabel>{t('dialog.question_set.option_value')}</FormLabel>
            <FormControl>
              <Input {...field} maxLength={QUESTION_SET_LIMITS.VALUE} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={control}
        name={`${path}.description`}
        render={({ field }) => (
          <FormItem>
            <FormLabel>{t('dialog.question_set.option_description')}</FormLabel>
            <FormControl>
              <Input
                {...field}
                value={field.value ?? ''}
                maxLength={QUESTION_SET_LIMITS.DESCRIPTION}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      {optionIndex === 0 && (
        <FormField
          control={control}
          name={`${path}.recommended`}
          render={({ field }) => (
            <FormItem className="flex flex-row items-center justify-between gap-4">
              <div className="space-y-0.5">
                <FormLabel>{t('dialog.question_set.recommended')}</FormLabel>
                <FormDescription>
                  {t('dialog.question_set.recommended_description')}
                </FormDescription>
              </div>
              <FormControl>
                <Switch
                  checked={Boolean(field.value)}
                  onCheckedChange={(checked) => {
                    field.onChange(checked);
                    onCommit();
                  }}
                />
              </FormControl>
            </FormItem>
          )}
        />
      )}

      <ShowIfFields name={`${path}.show_if`} questionIndex={questionIndex} onCommit={onCommit} />
    </div>
  );
};
